import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Dialog } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { parseTypedAmount } from '@/lib/number';

interface PondCreateModalProps {
  open: boolean;
  onClose: () => void;
  farmId: string;
}

interface PondFormData {
  name: string;
  area_ha: string;
  depth_m: string;
  pond_type: string;
  notes: string;
}

const POND_TYPE_OPTIONS = [
  { value: '', label: 'Select type' },
  { value: 'earthen', label: 'Earthen' },
  { value: 'lined', label: 'Lined' },
  { value: 'concrete', label: 'Concrete' },
];

export function PondCreateModal({ open, onClose, farmId }: PondCreateModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<PondFormData>({
    name: '',
    area_ha: '',
    depth_m: '',
    pond_type: '',
    notes: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof PondFormData, string>>>({});
  // The server's answer is about the name that was submitted; it can land after the user has edited.
  const [takenName, setTakenName] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: async (data: PondFormData) => {
      const payload: Record<string, unknown> = {
        name: data.name.trim(),
      };

      const area = parseTypedAmount(data.area_ha);
      if ('value' in area && area.value !== null) {
        payload.area_ha = area.value;
      }
      const depth = parseTypedAmount(data.depth_m);
      if ('value' in depth && depth.value !== null) {
        payload.depth_m = depth.value;
      }
      if (data.pond_type) {
        payload.pond_type = data.pond_type;
      }
      if (data.notes.trim()) {
        payload.notes = data.notes.trim();
      }

      return api.post(`/api/v1/farms/${farmId}/ponds`, payload);
    },
    onSuccess: () => {
      toast.success('Pond created successfully');
      queryClient.invalidateQueries({ queryKey: ['farm', 'overview', farmId] });
      queryClient.invalidateQueries({ queryKey: qk.ponds.list(farmId) });
      queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
      handleClose();
    },
    onError: (error: Error, submitted) => {
      // Check for unique constraint violation
      if (error.message.includes('already exists') || error.message.includes('unique')) {
        setTakenName(submitted.name.trim());
      } else {
        toast.error(error.message || 'Failed to create pond');
      }
    },
  });

  const handleClose = () => {
    setFormData({
      name: '',
      area_ha: '',
      depth_m: '',
      pond_type: '',
      notes: '',
    });
    setErrors({});
    setTakenName(null);
    onClose();
  };

  const handleChange = (field: keyof PondFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof PondFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Pond name is required';
    }

    // "0,5" is 0.5; anything that is not a clean number says so.
    const area = parseTypedAmount(formData.area_ha);
    if ('error' in area) {
      newErrors.area_ha = area.error;
    } else if (area.value !== null && area.value < 0) {
      newErrors.area_ha = 'Area must be a positive number';
    }

    const depth = parseTypedAmount(formData.depth_m);
    if ('error' in depth) {
      newErrors.depth_m = depth.error;
    } else if (depth.value !== null && depth.value < 0) {
      newErrors.depth_m = 'Depth must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      createMutation.mutate(formData);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} title="Add New Pond" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Pond Name"
          placeholder="e.g., Pond 1, A1, North Pond"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          error={
            errors.name ??
            (takenName !== null && formData.name.trim() === takenName
              ? 'A pond with this name already exists'
              : undefined)
          }
          autoFocus
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Area (hectares)"
            type="text"
            inputMode="decimal"
            placeholder="e.g., 0.5"
            value={formData.area_ha}
            onChange={(e) => handleChange('area_ha', e.target.value)}
            error={errors.area_ha}
          />

          <Input
            label="Depth (meters)"
            type="text"
            inputMode="decimal"
            placeholder="e.g., 1.5"
            value={formData.depth_m}
            onChange={(e) => handleChange('depth_m', e.target.value)}
            error={errors.depth_m}
          />
        </div>

        <Select
          label="Pond Type"
          value={formData.pond_type}
          onChange={(e) => handleChange('pond_type', e.target.value)}
          options={POND_TYPE_OPTIONS}
        />

        <Textarea
          label="Notes"
          placeholder="Any additional notes about this pond..."
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={3}
        />

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={createMutation.isPending}
            className="min-h-11"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={createMutation.isPending}
            className="min-h-11"
          >
            Create Pond
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
